
/**
 * Architecture Prompt Template
 * 
 * This file defines the STRUCTURE of the prompt.
 * It separates "Universal Rules" (Photography, Geometry, Output Quality)
 * from "Regional/Dynamic Data" (Materials, Details, Context).
 * 
 * Usage: The 'hydrator' function in useArchitecturePrompt will fill in the {{tokens}}.
 */

export const ARCHITECTURE_PROMPT_TEMPLATE = {
    // ------------------------------------------------------------------
    // 1. UNIVERSAL PERSONA & IDENTITY (The "Driver")
    // ------------------------------------------------------------------
    role: `Role: You are a Senior 3D Elevation Architect and Architectural Photographer.
[-- PRIMARY OBJECTIVE --]
Your task is to redesign this architectural elevation into the specified regional style.`,

    // ------------------------------------------------------------------
    // 2. UNIVERSAL CONSTRAINTS (The "Law")
    // ------------------------------------------------------------------
    constraints: [
        "INPUT IMAGE CONTEXT: The provided input image represents pure massing and structural geometry ONLY. COMPLETELY IGNORE its existing paint, materials, architectural style, or current visual look. Treat it as a blank volumetric canvas to apply the requested new style onto.",
        "GEOMETRY LOCK: The input image is the layout. You are painting TEXTURES onto this layout. Do not add or remove volume.",
        "TASK: Retain the EXACT GEOMETRY and MASSING of the input image. Surface-level material renovation only.",
        "DO NOT CHANGE THE SILHOUETTE.",
        "SURROUNDINGS: For any area OUTSIDE the building footprint (ground, pavement, curb, road shoulder), render it as clean, well-maintained urban context appropriate for the building's usage. Use a plain paved sidewalk and a neat concrete curb at the road edge. Do NOT extend the building into the ground plane or add elements that obscure the building base. The hard edge between building and ground and between pavement and road must remain clearly visible and unaltered."
    ],

    // ------------------------------------------------------------------
    // 3. DYNAMIC SECTIONS (The "Variables")
    // ------------------------------------------------------------------
    sections: {
        // A. Visual Identity (Style & Region)
        identity: `
            REGIONAL GUIDELINE: {{voice_instruction}}
            EXISTING MASSING: {{massing_description}}
            ROOF TYPE: {{roof_type}}
            STYLE REFERENCE: {{style_modifier}}
        `,

        // B. Materiality (The "Skin")
        materials: `
            MATERIAL RENOVATION SPEC:
            1. MAIN BODY ({{primary_proportion}} coverage): {{upper_material}}
            2. BASE/PLINTH: {{base_material}} (Must be distinctly darker and anchor the building)
            3. ACCENT FEATURES ({{accent_proportion}} coverage): {{wood_material}}
            
            COLOR PALETTE: {{palette_instruction}}
            Ensure main body uses Primary colors. Use Accent colors strictly for features (doors, window frames, small details).
            ARCHITECTURAL DETAILS: {{details_instruction}}
        `,

        // C. Fenestration (Windows & Openings)
        openings: `
            GLAZING: {{window_ratio}}
            WINDOWS: {{window_details}}
            OPENING STYLE: {{opening_style}}
        `,

        // D. Context & Atmosphere (The "Vibe")
        atmosphere: `
            SUBJECT: {{subject_description}}
            LIGHTING: {{lighting_description}}
            CONTEXT: {{context_description}}
        `,

        // E. Architectural Elements (The "Kit of Parts")
        elements: `
            ROOF EDGE: {{roof_edge}}
            WALL RELIEF: {{wall_relief}}
            BASE/ARCHES: {{base_arches}}
            FEATURE ACCENTS: {{feature_accents}}
        `
    },

    // ------------------------------------------------------------------
    // 4. UNIVERSAL OUTPUT QUALITY (The "Polish")
    // ------------------------------------------------------------------
    quality: `
        PHOTOGRAPHY SPECS: Shot on Canon EOS R5, 50mm lens, f/8 aperture, ISO 100. High resolution architectural photography, matte finish, weathered natural textures, imperfection, hyper-realistic, 8k, raw photo.
    `,

    // ------------------------------------------------------------------
    // 5. NEGATIVE PROMPT (The "Filter")
    // ------------------------------------------------------------------
    negative_universal: "blurry, low quality, distorted, watermark, text, signature, bad geometry, impossible architecture",

    // Dynamic negatives to be appended
    negative_dynamic_slots: [
        "{{forbidden_elements}}",  // From Database
        "{{style_negatives}}"      // From Style Logic (Traditional vs Modern)
    ]
} as const;
